import React, { useState } from "react";
import { Tab, Tabs } from "react-bootstrap";
import ApiKeysSection from "../components/settings/ApiKeysSection";
import { ApiKeyAccessLog, fetchAllActivity } from "../services/apiKeyService";
import "./apiKeysDark.css";

function formatDate(value: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

function statusCodeClass(code: number) {
  if (code >= 200 && code < 300) return "ak-badge-status-ok";
  if (code >= 400 && code < 500) return "ak-badge-status-warn";
  return "ak-badge-status-err";
}

const ActivityOverview: React.FC = () => {
  const [logs, setLogs] = React.useState<ApiKeyAccessLog[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setLogs(await fetchAllActivity(200));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  if (loading) return <div className="text-muted py-4">Loading activity…</div>;
  if (error) return <div className="ak-alert-danger py-4">{error}</div>;
  if (logs.length === 0) {
    return <div className="text-muted py-4">No API activity recorded yet.</div>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle mb-0">
        <thead>
          <tr>
            <th>Time</th>
            <th>Key</th>
            <th>Method</th>
            <th>Path</th>
            <th>Status</th>
            <th>IP</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log.id}>
              <td className="text-nowrap small">{formatDate(log.created_at)}</td>
              <td className="small">{log.api_key_name || "—"}</td>
              <td className="small">{log.method}</td>
              <td className="small text-truncate" style={{ maxWidth: 280 }}>{log.path}</td>
              <td className={`small ${statusCodeClass(log.status_code)}`}>{log.status_code}</td>
              <td className="small text-muted">{log.ip_address || "—"}</td>
              <td className="small text-muted">{log.duration_ms != null ? `${log.duration_ms}ms` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ApiKeys: React.FC = () => {
  const [activeTab, setActiveTab] = useState("keys");

  return (
    <div className="p-4 api-keys-page">
      <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
        <div>
          <h4 className="mb-1">API Keys</h4>
          <p className="text-muted mb-0">
            Central control plane for all API access, permissions, and activity.
          </p>
        </div>
        <a
          href="/API_CONNECTION_GUIDE.md"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-ak-outline btn-sm"
        >
          API Connection Guide
        </a>
      </div>

      <Tabs
        activeKey={activeTab}
        onSelect={(key) => key && setActiveTab(key)}
        className="mb-4"
        variant="pills"
      >
        <Tab eventKey="keys" title="Keys">
          <ApiKeysSection />
        </Tab>
        <Tab eventKey="activity" title="All Activity">
          <div className="ak-card p-3">
            <h5 className="mb-3">System-wide API Activity</h5>
            <ActivityOverview />
          </div>
        </Tab>
      </Tabs>
    </div>
  );
};

export default ApiKeys;
